/**
 * Fail-visible landing page for a malformed `?patient_id=` deep link.
 *
 * There is no patient registry in this app to confirm a given id refers to a
 * real patient (see `utils/patientId.ts`), so this covers the format-invalid
 * case: an empty/whitespace/free-text value that does not match either known
 * id shape. Rendered instead of {@link AppShell} so a bad link is never
 * indistinguishable from a real, brand-new empty patient (ADR-073).
 */
import React from "react";
import bbLogo from "../../Blackbird_Logo.png";

/** Props for {@link InvalidPatientIdNotice}. */
interface InvalidPatientIdNoticeProps {
  /** The raw, unparsed `patient_id` query-param value that failed validation. */
  patientId: string;
}

/**
 * Render a fail-visible notice for a malformed `patient_id` deep link.
 *
 * @param props - The offending raw patient id value.
 * @returns The notice element.
 */
export const InvalidPatientIdNotice: React.FC<InvalidPatientIdNoticeProps> = ({
  patientId,
}) => (
  <div
    data-testid="invalid-patient-id-notice"
    className="flex flex-col justify-center items-center h-screen bg-gray-50"
  >
    <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
      <img
        className="!w-40 h-auto mb-4 object-contain inline-block"
        src={bbLogo}
        alt="Precision Care Platform Logo"
      />
      <h2 className="text-xl font-semibold text-red-600 mb-4">
        Patient link not recognized
      </h2>
      <p className="text-gray-700 mb-2">
        The link you followed has an invalid patient id and can&apos;t be
        opened.
      </p>
      <p
        className="text-sm text-gray-500 mb-6 break-words"
        data-testid="invalid-patient-id-value"
      >
        &ldquo;{patientId}&rdquo;
      </p>
      <a
        href="/"
        className="inline-block px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
      >
        Go to home
      </a>
    </div>
  </div>
);

export default InvalidPatientIdNotice;
